import React from 'react';
import { Moment } from 'moment';
import { toMoment } from '@shared/utils/date';
import { capitalize } from '@shared/utils/string';
import { TimeZoneFormat } from '@shared/utils/constants';
import { formatDate } from '@client/utils/formatDate';
import styles from './styles/date.module.css';

type SortDirection = 'asc' | 'desc';

// Get an array of all the different filter options
export const getFilterOptions = <
	Data extends { [s: string]: string | number | null } | string | number | null
>(
	data: Array<Data>,
	keyOrKeys: string | string[] | null,
	sort: SortDirection,
	customCompare?: (a: any, b: any) => number,
) => {
	const values = data
		.map((row) => {
			if (row == null) {
				return null;
			}

			if (keyOrKeys && typeof row === 'object') {
				if (Array.isArray(keyOrKeys)){
					let val: { [s: string]: any } | string | number | null = null;
					keyOrKeys.forEach((key) => {
						if (val && typeof val === 'object') {
							val = val[key];

							return;
						}

						val = row[key];
					});

					return val;	
				}

				return typeof row === 'object' && row !== null ? row[keyOrKeys] : null;
			}

			return row;
		})
		.filter((v): v is string => v != null && v !== '');

	const unique = [...new Set(values)];

	if (sort === 'asc') {
		unique.sort(customCompare);
	} else if (sort === 'desc') {
		unique.sort(customCompare).reverse();
	}

	return unique.map((option) => ({
		text: capitalize(option),
		value: option,
	}));
};

export const renderUtcDate = (
	date: Moment | null,
	...props: any[]
) => (date == null ? null : (
	<div className={styles.utcDateWrapper}>
		<div>{formatDate(date, ...props)}</div>
		<div className={styles.utcTag}>UTC</div>
	</div>
));

export const filterEquals = (
	keyOrKeys: string | string[],
) => (value: any, record: Record<string, any>) => {
	if (Array.isArray(keyOrKeys)){
		let val: { [s: string]: any } | string | number | null = null;
		keyOrKeys.forEach((key) => {
			if (val && typeof val === 'object') {
				val = val[key];

				return;
			}

			val = record[key];
		});

		return val === value;	
	}

	return record[keyOrKeys as keyof typeof record] === value || record[keyOrKeys as keyof typeof record] === '';
};

// rawValues is a boolean indicating whether the data array contains
// objects where we want the value of key or just an array of values
export const getFilterProps = <
	Data extends { [s: string]: any } | string | number | null,
>(
	data: Array<Data>,
	type: 'select' | 'dateRange',
	keyOrKeys: string | string[],
	sort: SortDirection = 'asc',
	rawValues = false,
	customCompare?: (a: Data, b: Data) => number,
) => {
	switch (type) {
		case 'select':
			return {
				filters: getFilterOptions(
					data,
					rawValues ? null : keyOrKeys,
					sort,
					customCompare,
				),
				onFilter: filterEquals(keyOrKeys),
				keyOrKeys,
			};

		default:
			return {};
	}
};

export const renderDate = (
	format?: string,
	timeZoneFormat?: TimeZoneFormat,
) => (text: string) => text && formatDate(
	toMoment(text),
	format,
	true,
	timeZoneFormat,
);

export const invoiceIdentifierCompare = (a: string, b: string) => {
	const voyageNameA = a.split('-').slice(0, -1).join('-');
	const hireInvoiceNumA = Number(a.split('-').slice(-1)[0]);
	const voyageNameB = b.split('-').slice(0, -1).join('-');
	const hireInvoiceNumB = Number(b.split('-').slice(-1)[0]);

	if (voyageNameA !== voyageNameB) {
		return voyageNameA.localeCompare(voyageNameB);
	}

	return hireInvoiceNumA - hireInvoiceNumB;
};
